import { DEFAULT_CURRENCY } from "@repo/shared";
import type {
  AssetClass,
  CreateAssetClassRequest,
  LocalizedString,
  PortfolioCollectionCalculationsMode,
} from "@repo/shared/types";
import { AlertCircle, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCreateAssetClass,
  useGetAssetClassStatistics,
  useUpdateAssetClass,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";

import {
  InstrumentSearchDialog,
  type InstrumentSelectResult,
} from "../products/instrument-search-dialog";
import { T } from "./asset-class-dialog.i18n";

type AssetClassDialogProps = {
  open: boolean;
  portfolioCollectionId: string;
  assetClass: AssetClass | null;
  onClose: () => void;
  currency?: string;
  riskFreeRatePct?: number;
  calculationsMode?: PortfolioCollectionCalculationsMode;
};

export function AssetClassDialog({
  open,
  portfolioCollectionId,
  assetClass,
  onClose,
  currency = DEFAULT_CURRENCY,
  riskFreeRatePct = 2.5,
  calculationsMode = "asset_classes",
}: AssetClassDialogProps) {
  // QP-164 — expected return/volatility drive calculations only in Asset
  // Classes mode; in Products mode the product's own fields are authoritative
  // (see the product dialog's "Calculation input" tab) and these are hidden.
  const showCalculationInputs = calculationsMode === "asset_classes";
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const editingAssetClassName = assetClass ? localize(assetClass.nameI18n) : "";

  const createAssetClass = useCreateAssetClass();
  const updateAssetClass = useUpdateAssetClass();
  const getStatistics = useGetAssetClassStatistics();
  const { mutate: fetchStatistics, reset: resetStatistics } = getStatistics;

  const [error, setError] = useState<string | null>(null);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [linkedInstrument, setLinkedInstrument] = useState<{
    ticker: string;
    dataset: string;
    name: string;
  } | null>(null);
  // Statistics state
  const [apiStats, setApiStats] = useState<{
    expectedReturn: number | null;
    expectedVolatility: number | null;
  }>({ expectedReturn: null, expectedVolatility: null });

  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [formData, setFormData] = useState({
    ticker: "",
    dataset: "",
    expectedReturn: "",
    expectedVolatility: "",
    equitySharePct: "",
  });

  useEffect(() => {
    if (assetClass) {
      setName(localize(assetClass.nameI18n));
      setNameI18n(assetClass.nameI18n);
      setFormData({
        ticker: assetClass.ticker || "",
        dataset: assetClass.dataset || "",
        expectedReturn:
          assetClass.expectedReturn != null ? assetClass.expectedReturn.toFixed(2) : "",
        expectedVolatility:
          assetClass.expectedVolatility != null ? assetClass.expectedVolatility.toFixed(2) : "",
        equitySharePct:
          assetClass.equitySharePct != null ? assetClass.equitySharePct.toFixed(2) : "",
      });
      // Set linked instrument if asset class has ticker and dataset
      if (assetClass.ticker && assetClass.dataset) {
        setLinkedInstrument({
          ticker: assetClass.ticker,
          dataset: assetClass.dataset,
          name: localize(assetClass.nameI18n),
        });
      } else {
        setLinkedInstrument(null);
      }
    } else {
      setName("");
      setNameI18n({});
      setFormData({
        ticker: "",
        dataset: "",
        expectedReturn: "",
        expectedVolatility: "",
        equitySharePct: "",
      });
      setLinkedInstrument(null);
    }
    setError(null);
    setApiStats({ expectedReturn: null, expectedVolatility: null });
    resetStatistics();
  }, [assetClass, open, localize, resetStatistics]);

  // Fetch statistics when linked instrument changes
  useEffect(() => {
    if (!linkedInstrument) {
      setApiStats({ expectedReturn: null, expectedVolatility: null });
      resetStatistics();
      return;
    }

    fetchStatistics(
      {
        collectionId: portfolioCollectionId,
        data: {
          dataset: linkedInstrument.dataset,
          ticker: linkedInstrument.ticker,
          currencyIso: currency,
          riskFreeRatePct,
        },
      },
      {
        onSuccess: (result) => {
          setApiStats({
            expectedReturn: result.expectedReturn,
            expectedVolatility: result.expectedVolatility,
          });
        },
        onError: () => {
          setApiStats({ expectedReturn: null, expectedVolatility: null });
        },
      },
    );
  }, [
    linkedInstrument,
    currency,
    riskFreeRatePct,
    portfolioCollectionId,
    fetchStatistics,
    resetStatistics,
  ]);

  const handleInstrumentSelect = (result: InstrumentSelectResult) => {
    const { instrument, dataset, metadata, error: metadataError } = result;
    setError(null);

    // If metadata fetch failed
    if (!metadata) {
      if (metadataError) {
        setError(`${metadataError} ${t.notLinkedError}`);
      }
      // Use search data but DON'T set ticker/dataset (not linking to database)
      setName(instrument.name);
      setFormData((prev) => ({
        ...prev,
        ticker: "",
        dataset: "",
      }));
      setLinkedInstrument(null);
      return;
    }

    // Use metadata from API
    setName(metadata.name || instrument.name);
    setFormData((prev) => ({
      ...prev,
      ticker: instrument.ticker,
      dataset: dataset,
    }));

    setLinkedInstrument({
      ticker: instrument.ticker,
      dataset: dataset,
      name: metadata.name || instrument.name,
    });
  };

  const handleRemoveInstrument = () => {
    setLinkedInstrument(null);
    setFormData((prev) => ({
      ...prev,
      ticker: "",
      dataset: "",
    }));
  };

  // Copy API values to form
  const handleCopyFromApi = () => {
    setFormData((prev) => ({
      ...prev,
      expectedReturn: apiStats.expectedReturn?.toFixed(2) || "",
      expectedVolatility: apiStats.expectedVolatility?.toFixed(2) || "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Build the full i18n object with the primary language value
    const fullNameI18n: LocalizedString = {
      ...nameI18n,
      [userLanguage]: name,
    };

    // QP-164 — the name is used as the correlation-matrix CSV row/column ID,
    // which is ";"-delimited. The server enforces this too; checked here for
    // immediate feedback without a round trip.
    if (Object.values(fullNameI18n).some((v) => typeof v === "string" && v.includes(";"))) {
      setError(t.nameCannotContainSemicolon);
      return;
    }

    // Create and update are both full replaces: "" (after handleRemoveInstrument)
    // clears the link, the same as leaving the field out.
    const submitData: CreateAssetClassRequest = {
      nameI18n: fullNameI18n,
      ticker: formData.ticker,
      dataset: formData.dataset,
      expectedReturn: formData.expectedReturn ? parseFloat(formData.expectedReturn) : undefined,
      expectedVolatility: formData.expectedVolatility
        ? parseFloat(formData.expectedVolatility)
        : undefined,
      equitySharePct: formData.equitySharePct ? parseFloat(formData.equitySharePct) : undefined,
    };

    const callbacks = {
      onSuccess: () => {
        onClose();
      },
      onError: (err: Error) => {
        setError(err.message || t.failedToSave);
      },
    };

    if (assetClass) {
      updateAssetClass.mutate(
        { collectionId: portfolioCollectionId, acId: assetClass.id, data: submitData },
        callbacks,
      );
    } else {
      createAssetClass.mutate({ collectionId: portfolioCollectionId, data: submitData }, callbacks);
    }
  };

  const loading = createAssetClass.isPending || updateAssetClass.isPending;
  const statsLoading = getStatistics.isPending;
  const statsError = getStatistics.error?.message || null;

  const hasLinkedInstrument = linkedInstrument !== null;
  const hasApiStats = apiStats.expectedReturn !== null || apiStats.expectedVolatility !== null;

  const missingStats =
    showCalculationInputs &&
    (formData.expectedReturn.trim() === "" || formData.expectedVolatility.trim() === "");

  return (
    <>
      <Dialog open={open} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {assetClass
                ? editingAssetClassName
                  ? `${t.editTitle} (${editingAssetClassName})`
                  : t.editTitle
                : t.createTitle}
            </DialogTitle>
            <DialogDescription>
              {assetClass ? t.editDescription : t.createDescription}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md mb-4">
                {error}
              </div>
            )}

            <div className="space-y-4 py-4">
              {/* Instrument Search Section */}
              <div className="space-y-2">
                <Label>{t.linkToInstrument}</Label>
                {linkedInstrument ? (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{linkedInstrument.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {linkedInstrument.ticker} ({linkedInstrument.dataset})
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      tooltip={t.remove}
                      onClick={handleRemoveInstrument}
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    data-dialog-guard-ignore
                    type="button"
                    variant="outline"
                    onClick={() => setSearchDialogOpen(true)}
                    disabled={loading}
                    className="w-full"
                  >
                    {t.searchInstrument}
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">{t.linkHint}</p>
              </div>

              <LocalizedInput
                id="name"
                label={t.name}
                value={name}
                i18nValue={nameI18n}
                onChange={setName}
                onI18nChange={setNameI18n}
                supportedLanguages={tenantSupportedLanguages}
                placeholder={t.namePlaceholder}
                required
                disabled={loading}
              />

              {/* Statistics Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t.statistics}</Label>
                  {showCalculationInputs && hasLinkedInstrument && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCopyFromApi}
                      disabled={loading || statsLoading || !hasApiStats}
                      className="gap-2"
                    >
                      {t.copyFromApi}
                    </Button>
                  )}
                </div>

                {/* Loading state for API data */}
                {showCalculationInputs && statsLoading && hasLinkedInstrument && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span className="text-sm text-muted-foreground">{t.loadingApi}</span>
                  </div>
                )}

                {/* Error state for API data */}
                {showCalculationInputs && statsError && hasLinkedInstrument && (
                  <div className="flex items-center gap-2 p-2 bg-destructive/10 text-destructive rounded-md text-sm">
                    <AlertCircle className="h-4 w-4" />
                    {statsError}
                  </div>
                )}

                <Table variant="data">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.statistic}</TableHead>
                      <TableHead className="text-right">{t.apiValue}</TableHead>
                      <TableHead className="text-right">{t.storedValue}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {showCalculationInputs && (
                      <>
                        {/* Expected Return Row */}
                        <TableRow>
                          <TableCell className="font-medium">{t.expectedReturn}</TableCell>
                          <TableCell className="text-right">
                            {statsLoading ? (
                              <span className="text-muted-foreground">...</span>
                            ) : hasLinkedInstrument ? (
                              apiStats.expectedReturn !== null ? (
                                <span>{apiStats.expectedReturn.toFixed(2)}%</span>
                              ) : (
                                <span className="text-muted-foreground">N/A</span>
                              )
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Input
                                type="number"
                                step="0.01"
                                className="w-24 text-right"
                                placeholder="0.00"
                                value={formData.expectedReturn}
                                onChange={(e) =>
                                  setFormData({ ...formData, expectedReturn: e.target.value })
                                }
                                disabled={loading}
                              />
                              <span className="text-muted-foreground">%</span>
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* Expected Volatility Row */}
                        <TableRow>
                          <TableCell className="font-medium">{t.expectedVolatility}</TableCell>
                          <TableCell className="text-right">
                            {statsLoading ? (
                              <span className="text-muted-foreground">...</span>
                            ) : hasLinkedInstrument ? (
                              apiStats.expectedVolatility !== null ? (
                                <span>{apiStats.expectedVolatility.toFixed(2)}%</span>
                              ) : (
                                <span className="text-muted-foreground">N/A</span>
                              )
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                className="w-24 text-right"
                                placeholder="0.00"
                                value={formData.expectedVolatility}
                                onChange={(e) =>
                                  setFormData({ ...formData, expectedVolatility: e.target.value })
                                }
                                disabled={loading}
                              />
                              <span className="text-muted-foreground">%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                    {/* Equity Share Row (GAP-028 — feeds the goal-level equity share chart's risk-bound check) */}
                    <TableRow>
                      <TableCell className="font-medium">{t.equityShare}</TableCell>
                      <TableCell className="text-right">
                        <span className="text-muted-foreground">-</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            className="w-24 text-right"
                            placeholder="0.00"
                            value={formData.equitySharePct}
                            onChange={(e) =>
                              setFormData({ ...formData, equitySharePct: e.target.value })
                            }
                            disabled={loading}
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {showCalculationInputs && (
                  <p className="text-xs text-muted-foreground">
                    {hasLinkedInstrument ? t.statsHintLinked : t.statsHintManual}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <DialogCancel disabled={loading}>{t.cancel}</DialogCancel>
              <Button type="submit" disabled={missingStats} loading={loading}>
                {assetClass ? t.save : t.addAssetClass}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <InstrumentSearchDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        onSelect={handleInstrumentSelect}
      />
    </>
  );
}
